import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  ChannelEnum,
  CollaboratorInvitation,
  CollaboratorInvitationAcceptanceResponse,
} from "@models/collaborator";
import { Page } from "@models/page";
import { PaginatorState } from "primeng/paginator";
import { catchError, of, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";
import { SnackBarService } from "./snack-bar.service";

@Injectable({
  providedIn: "root",
})
export class CollaboratorsService {
  private readonly apiUrl = environment.API_BACKEND;

  constructor(
    private readonly http: HttpClient,
    private snackbarService: SnackBarService,
    private readonly authService: AuthService,
  ) {}

  acceptInvitation(token: string) {
    return this.http
      .get<CollaboratorInvitationAcceptanceResponse>(
        `${this.apiUrl}/collaborator_invitations/${token}/accept`,
      )
      .pipe(
        tap((response) => {
          this.authService.updateLocalStorageUserInfo(response.user);
          this.snackbarService.openSnackBar(
            "Convite aceito com sucesso",
            "Fechar",
            "success",
          );
        }),
        catchError((err) => {
          this.snackbarService.openSnackBar(
            "Falha ao aceitar convite",
            "Fechar",
            "error",
          );
          return err;
        }),
      );
  }

  resendInvitation(invitation: CollaboratorInvitation) {
    const body = {
      channel: ChannelEnum.EMAIL,
      user_phone_number: invitation.phone,
    };

    return this.http
      .post(
        `${this.apiUrl}/collaborator_invitations/${invitation.id}/resend`,
        body,
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite reenviado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          this.snackbarService.openSnackBar(
            "Falha ao reenviar convite",
            "Fechar",
            "error",
          );
          return err;
        }),
      );
  }

  getUserInvitations(pageEvent?: PaginatorState) {
    const page = pageEvent?.page ?? 0;

    const params = new HttpParams()
      .set("include_accepted", true)
      .set("include_expired", false)
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    return this.http
      .get<
        Page<CollaboratorInvitation>
      >(`${this.apiUrl}/collaborator_invitations/my_invitations`, { params })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<CollaboratorInvitation>),
        ),
      );
  }

  revokeInvitation(invitationId: number) {
    return this.http
      .post(
        `${this.apiUrl}/collaborator_invitations/${invitationId}/revoke`,
        {},
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite revogado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          this.snackbarService.openSnackBar(
            "Falha ao revogar convite",
            "Fechar",
            "error",
          );
          return err;
        }),
      );
  }
}
